import * as React from "react";
import {RaisedButton} from "../common/RaisedButton";
import {defaultButtonStyle} from "../common/buttonStyles";
import {navigateTo} from "../../../navigateTo";
import {FormattedMessage} from "react-intl";
import {setCurrentTabAction} from "../portfolio/PortfolioActions";
import {PortfolioTab} from "../portfolio/PortfolioPage";
import {useDispatch} from "react-redux";
import {useEffect, useState} from "react";
import {IPlanAssetTrend, PortfolioApi} from "../../api/PortfolioApi";
import {calculateAssetTrend} from "./HomeUtils";
import {IQuarterAndYear} from "../../utils/dateUtil";
import {useNavigate} from "react-router-dom";
import {AnyThunkDispatch} from "../../../commonTypes";

export const ASSET_TRENDS_DESCRIPTION = "See what drove the total market value changes over the past quarters";
export const HomeAssetTrends: React.FunctionComponent = () => {
    const [assetTrends, setAssetTrends] = useState<IPlanAssetTrend[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        PortfolioApi.getCurrentPlanAssetTrends().then(response => {
            setAssetTrends(response);
        });
    }, []);

    const dispatch: AnyThunkDispatch = useDispatch();
    const renderAssetsButton = () => {
        return <RaisedButton
            className="home-insights__insights-button"
            style={ defaultButtonStyle }
            onClick={() => {
                dispatch(setCurrentTabAction(PortfolioTab.ASSET_ALLOCATION));
                navigateTo("/portfolio", navigate);
            }}
            primary={true}
        >
            <FormattedMessage id="home.assets-title" defaultMessage="Assets" />
        </RaisedButton>;
    };

    const getTableValuesClass = (label: string, value: string | undefined) => {
        const isNegative = value && value.toString().startsWith("-");
        const textColor = isNegative ? "red" : "";

        if (label === "Change In Market Value") {
            return `home-assets__first-row-value ${textColor}`;
        }
        if (label === "Percent Change") {
            return `home-assets__row-value home-assets__last-row ${textColor}`;
        }
        return `home-assets__row-value ${textColor}`;
    };

    const getRowLabelClass = (label: string) => {
        return label === "Percent Change" ? "home-assets__last-row" : "";
    };

    const renderRow = (label: string,
                       translationId: string,
                       value: string,
                       priorValue: string | undefined) => {
        return <tr>
            <td className={getRowLabelClass(label)}>
                <span className="home-assets__row-label">
                    <FormattedMessage id={translationId} defaultMessage={label}/>
                </span>
            </td>
            <td className={getTableValuesClass(label, value)}>{value}</td>
            {priorValue && <td className={getTableValuesClass(label, priorValue)}>{priorValue}</td>}
        </tr>;
    };

    const renderTimePeriod = (timePeriod: IQuarterAndYear) => {
        if (!timePeriod) {
            return null;
        }
        const translationId = `home.asset.period.${timePeriod.quarter}`;
        return <><FormattedMessage id={translationId} defaultMessage={timePeriod.quarter}/> {timePeriod.year}</>;
    };

    const renderTable = () => {
        const recentAssetTrend = calculateAssetTrend(assetTrends[0], assetTrends[1]);
        const previousAssetTrend = calculateAssetTrend(assetTrends[1], assetTrends[2]);
        const displayInflowOutFlow = recentAssetTrend?.hasInFlowOutFlowValues
            && (!previousAssetTrend || previousAssetTrend?.hasInFlowOutFlowValues);

        return <table className="home-assets__table">
                <thead>
                <tr>
                    <th className="home-assets__table-row-header"/>
                    <th className="home-assets__table-row-header">{renderTimePeriod(recentAssetTrend!.timePeriod)}</th>
                    <th className="home-assets__table-row-header">
                        {previousAssetTrend && renderTimePeriod(previousAssetTrend!.timePeriod)}
                    </th>
                </tr>
                </thead>
                <tbody>
                    {renderRow("Change In Market Value",
                        "home.asset.change-in-market-value",
                        recentAssetTrend!.changeInMarketValue,
                        previousAssetTrend?.changeInMarketValue
                    )}
                    {renderRow("Beginning Market Value",
                        "home.asset.beginning-market-value",
                        recentAssetTrend!.beginningMarketValue,
                        previousAssetTrend?.beginningMarketValue
                    )}
                    {displayInflowOutFlow && renderRow("Inflows",
                            "home.asset.inflows",
                            recentAssetTrend!.totalInflows!,
                            previousAssetTrend?.totalInflows ? (previousAssetTrend.totalInflows) : undefined)}
                    {displayInflowOutFlow && renderRow("Outflows",
                            "home.asset.outflows",
                            recentAssetTrend!.totalOutflows!,
                            previousAssetTrend?.totalOutflows ? previousAssetTrend.totalOutflows : undefined)}
                    {displayInflowOutFlow && renderRow("Investment Earnings",
                            "home.asset.investment.earnings",
                            recentAssetTrend!.investmentEarnings!,
                            previousAssetTrend?.investmentEarnings ? previousAssetTrend.investmentEarnings : undefined)}
                    {renderRow("Ending Market Value",
                        "home.asset.ending-market-value",
                        recentAssetTrend!.endingMarketValue,
                        previousAssetTrend?.endingMarketValue)}
                    {renderRow("Percent Change",
                        "home.asset.percent-change",
                        recentAssetTrend!.percentChange,
                        previousAssetTrend?.percentChange)}
                </tbody>
            </table>;
    };

    const renderContent = () => {
        if (!assetTrends || assetTrends.length === 0) {
            return null;
        }
        return <div className="home-insights__container" data-testid="home-assets-trends__container">
            <div className="home-insights__title-container">
                <div className="home-insights__title home-assets__title-container">
                    <FormattedMessage id="home.asset.title" defaultMessage="Asset Trends"/>
                </div>
                <div className="home-insights__description">
                    <FormattedMessage id="home.asset.description" defaultMessage={ASSET_TRENDS_DESCRIPTION}/>
                </div>
                <div className="home-insights__button-container">{renderAssetsButton()}</div>
            </div>
            <div className="home-assets__table-container">
                <div className="home-assets_table-title">
                    <h2><FormattedMessage id="home.asset.asset-value-trends" defaultMessage="Asset Value Trends"/></h2>
                </div>
                {renderTable()}
            </div>
        </div>;
    };

    return renderContent();
};